import { useEffect, useState } from 'react';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';

export interface UseOrgAdminResult {
  isOrgAdmin: boolean;
  loading: boolean;
}

interface CacheEntry {
  clientRef: ApiClient;
  promise: Promise<boolean>;
}

// Bounded Map keyed by orgId. Each entry carries the ApiClient reference it was
// created against; a mismatch on lookup is treated as a cache miss.
//
// That mismatch check does NOT auto-invalidate across an impersonation switch:
// `ApiClientsProvider` deliberately keeps its clients and resolves the bearer
// per-request through `tokenProvider`, so the reference is stable across the
// switch. Cross-identity invalidation comes from `resetOrgAdminCache()`, wired
// into `useSchemaTypeCacheReset`. EAINT-798.
const MAX_CACHE_ENTRIES = 8;
const cache = new Map<string, CacheEntry>();

/** Clear the org-admin cache for all orgs. Exported for tests and logout flows. */
export function resetOrgAdminCache(): void {
  cache.clear();
}

function evictIfFull(): void {
  if (cache.size >= MAX_CACHE_ENTRIES) {
    const firstKey = cache.keys().next().value;
    if (firstKey !== undefined) cache.delete(firstKey);
  }
}

function fetchIsOrgAdmin(client: ApiClient, orgId: string): Promise<boolean> {
  const existing = cache.get(orgId);
  if (existing?.clientRef === client) {
    return existing.promise;
  }

  evictIfFull();

  const promise = client
    .get<{ allowed: boolean }>(
      `/admin/org-check?orgId=${encodeURIComponent(orgId)}`,
    )
    .then((resp) => Boolean(resp?.allowed))
    .catch(() => {
      const current = cache.get(orgId);
      if (current?.clientRef === client) {
        cache.delete(orgId);
      }
      return false;
    });

  cache.set(orgId, { clientRef: client, promise });
  return promise;
}

/**
 * Returns whether the authenticated user is an org admin for the given org.
 * Result is cached per `(session, orgId)` pair — bounded to 8 orgs — so
 * repeated calls with the same org do not trigger additional network requests.
 *
 * When `orgId` is not provided, the hook immediately returns
 * `{ isOrgAdmin: false, loading: false }` (callers must supply the org ID).
 *
 * Fails closed: on network error, returns `isOrgAdmin: false`.
 * This is a UI gate only — the backend still enforces authorization.
 */
export function useOrgAdmin(orgId?: string): UseOrgAdminResult {
  const apiClients = useApiClientsOrNull();
  const [isOrgAdminState, setIsOrgAdmin] = useState(false);
  const [loading, setLoading] = useState(Boolean(orgId));

  useEffect(() => {
    if (!orgId || !apiClients?.graphClient) {
      setIsOrgAdmin(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetchIsOrgAdmin(apiClients.graphClient, orgId).then((allowed) => {
      if (cancelled) return;
      setIsOrgAdmin(allowed);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [apiClients, orgId]);

  return { isOrgAdmin: isOrgAdminState, loading };
}
